import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { rentalService } from '../../services/rentalService';
import { refillService } from '../../services/refillService';
import { VisitType, VisitPriority, VisitStatus, CreateVisitScheduleData } from '../../types/rental';
import { RefillForm } from './RefillForm';
import { VisitPrintDocument } from './VisitPrintDocument';
import { createRoot } from 'react-dom/client';

interface VisitScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// ── style tokens ──────────────────────────────────────────────────────────────
const inputStyle: React.CSSProperties = {
  background: '#f4efe2',
  border: '1px solid rgba(92,78,51,0.25)',
  borderRadius: 4,
  padding: '9px 12px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 12,
  color: '#5b5a52',
  outline: 'none',
  width: '100%',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontFamily: "'Inter', sans-serif",
  fontSize: 9,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  color: '#6b6a5c',
  marginBottom: 6,
};

const thStyle: React.CSSProperties = {
  padding: '10px 14px',
  textAlign: 'left',
  fontFamily: "'Inter', sans-serif",
  fontSize: 9,
  letterSpacing: '0.15em',
  textTransform: 'uppercase',
  color: '#6b6a5c',
  borderBottom: '1px solid rgba(92,78,51,0.15)',
  background: '#faf7ee',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '11px 14px',
  borderBottom: '1px solid rgba(239,232,213,0.6)',
  fontFamily: "'Inter', sans-serif",
  fontSize: 12,
  color: '#5b5a52',
  whiteSpace: 'nowrap',
};

const primaryBtn: React.CSSProperties = {
  background: 'linear-gradient(135deg, #d97706, #b45309)',
  color: '#1c2033',
  border: 'none',
  borderRadius: 4,
  padding: '9px 22px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 10,
  letterSpacing: '0.15em',
  textTransform: 'uppercase',
  cursor: 'pointer',
  fontWeight: 600,
};

const ghostBtn: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid rgba(92,78,51,0.25)',
  borderRadius: 4,
  padding: '9px 22px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 10,
  letterSpacing: '0.15em',
  textTransform: 'uppercase',
  color: '#6b6a5c',
  cursor: 'pointer',
};

const smallPrimaryBtn: React.CSSProperties = {
  background: 'linear-gradient(135deg, #d97706, #b45309)',
  color: '#1c2033',
  border: 'none',
  borderRadius: 4,
  padding: '4px 12px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 9,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  cursor: 'pointer',
  fontWeight: 600,
};

const smallGhostBtn: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid rgba(92,78,51,0.25)',
  borderRadius: 4,
  padding: '4px 12px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 9,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: '#6b6a5c',
  cursor: 'pointer',
};

const smallGreenBtn: React.CSSProperties = {
  background: 'rgba(100,200,100,0.12)',
  border: '1px solid rgba(100,200,100,0.25)',
  borderRadius: 4,
  padding: '4px 12px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 9,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: '#6fc96f',
  cursor: 'pointer',
};

const dangerBtn: React.CSSProperties = {
  background: 'rgba(192,80,77,0.12)',
  border: '1px solid rgba(192,80,77,0.3)',
  borderRadius: 4,
  padding: '4px 14px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 9,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: '#c0504d',
  cursor: 'pointer',
};

const getVisitTypeStyle = (type: VisitType): React.CSSProperties => {
  const base: React.CSSProperties = { padding: '3px 10px', borderRadius: 12, fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', display: 'inline-block' };
  switch (type) {
    case VisitType.REFILL:        return { ...base, background: 'rgba(90,140,201,0.15)', color: '#5a8cc9', border: '1px solid rgba(90,140,201,0.3)' };
    case VisitType.SERVICE:       return { ...base, background: 'rgba(201,130,50,0.15)', color: '#c98232', border: '1px solid rgba(201,130,50,0.3)' };
    case VisitType.SCENT_CHANGE:  return { ...base, background: 'rgba(160,100,201,0.15)', color: '#a064c9', border: '1px solid rgba(160,100,201,0.3)' };
    case VisitType.SAMPLE_DELIVERY: return { ...base, background: 'rgba(100,200,100,0.12)', color: '#6fc96f', border: '1px solid rgba(100,200,100,0.25)' };
    default:                      return { ...base, background: 'rgba(92,78,51,0.15)', color: '#6b6a5c', border: '1px solid rgba(92,78,51,0.25)' };
  }
};

const getStatusStyle = (status: VisitStatus): React.CSSProperties => {
  const base: React.CSSProperties = { padding: '3px 10px', borderRadius: 12, fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', display: 'inline-block' };
  switch (status) {
    case VisitStatus.SCHEDULED:      return { ...base, background: 'rgba(201,165,50,0.12)', color: '#c9a532', border: '1px solid rgba(201,165,50,0.3)' };
    case VisitStatus.IN_PROGRESS:    return { ...base, background: 'rgba(90,140,201,0.15)', color: '#5a8cc9', border: '1px solid rgba(90,140,201,0.3)' };
    case VisitStatus.COMPLETED:      return { ...base, background: 'rgba(100,200,100,0.12)', color: '#6fc96f', border: '1px solid rgba(100,200,100,0.25)' };
    case VisitStatus.NEEDS_FOLLOWUP: return { ...base, background: 'rgba(192,80,77,0.12)', color: '#c0504d', border: '1px solid rgba(192,80,77,0.3)' };
    default:                         return { ...base, background: 'rgba(92,78,51,0.15)', color: '#6b6a5c', border: '1px solid rgba(92,78,51,0.25)' };
  }
};

// Inner sub-modal wrapper
const SubModal: React.FC<{ children: React.ReactNode; zIndex?: number }> = ({ children, zIndex = 1060 }) => (
  <div style={{ position: 'fixed', inset: 0, background: 'rgba(7,7,15,0.85)', zIndex, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
    {children}
  </div>
);

export const VisitScheduleModal: React.FC<VisitScheduleModalProps> = ({ isOpen, onClose }) => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'upcoming' | 'schedule'>('upcoming');
  const [daysAhead, setDaysAhead] = useState(7);
  const [selectedVisit, setSelectedVisit] = useState<any>(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [showFollowupModal, setShowFollowupModal] = useState(false);
  const [showRefillFormModal, setShowRefillFormModal] = useState(false);
  const [refillVisitData, setRefillVisitData] = useState<any>(null);

  const [visitForm, setVisitForm] = useState<Partial<CreateVisitScheduleData & { machineSelection: string[] }>>({
    contractId: '',
    machineSelection: [],
    machineIds: [],
    visitType: VisitType.REFILL,
    priority: VisitPriority.NORMAL,
    scheduledDate: new Date().toISOString().split('T')[0],
    requestNotes: '',
    fromScent: '',
    toScent: ''
  });

  const [statusForm, setStatusForm] = useState<{
    visitStatus: VisitStatus;
    issueResolved: boolean;
    followupRequired: boolean;
    completionNotes: string;
    machineSelection?: string[];
    machineIds?: string[];
  }>({
    visitStatus: VisitStatus.IN_PROGRESS,
    issueResolved: false,
    followupRequired: false,
    completionNotes: '',
    machineSelection: [],
    machineIds: []
  });

  const [rescheduleForm, setRescheduleForm] = useState({
    newDate: new Date().toISOString().split('T')[0],
    reason: ''
  });

  const [followupForm, setFollowupForm] = useState<Partial<CreateVisitScheduleData & { machineSelection: string[] }>>({
    contractId: '',
    machineSelection: [],
    machineIds: [],
    visitType: VisitType.SERVICE,
    priority: VisitPriority.NORMAL,
    scheduledDate: new Date().toISOString().split('T')[0],
    requestNotes: '',
    fromScent: '',
    toScent: ''
  });

  const { data: upcomingVisitsData, isLoading: upcomingLoading } = useQuery({
    queryKey: ['upcoming-visits', daysAhead],
    queryFn: async () => {
      const response = await rentalService.getUpcomingVisits(daysAhead);
      return response.data;
    },
    enabled: isOpen && activeTab === 'upcoming'
  });

  const { data: contractsData } = useQuery({
    queryKey: ['rental-contracts', { status: 'ACTIVE' }],
    queryFn: () => rentalService.getRentalContracts({ status: 'ACTIVE' as any, limit: 100 }),
    enabled: isOpen && activeTab === 'schedule'
  });

  const { data: selectedContract } = useQuery({
    queryKey: ['rental-contract', visitForm.contractId],
    queryFn: () => rentalService.getRentalContract(visitForm.contractId!),
    enabled: !!visitForm.contractId && activeTab === 'schedule'
  });

  const { data: selectedVisitContract } = useQuery({
    queryKey: ['rental-contract', selectedVisit?.contract?.id],
    queryFn: () => rentalService.getRentalContract(selectedVisit!.contract!.id),
    enabled: !!selectedVisit?.contract?.id && showStatusModal
  });

  const { data: currentScentData } = useQuery({
    queryKey: ['current-scent', visitForm.contractId],
    queryFn: () => rentalService.getCurrentScent(visitForm.contractId!),
    enabled: !!visitForm.contractId && visitForm.visitType === VisitType.SCENT_CHANGE && activeTab === 'schedule'
  });

  const scheduleVisitMutation = useMutation({
    mutationFn: rentalService.scheduleVisit,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['upcoming-visits'] });
      queryClient.invalidateQueries({ queryKey: ['visits-this-week-count'] });
      setVisitForm({
        contractId: '', machineSelection: [], machineIds: [],
        visitType: VisitType.REFILL, priority: VisitPriority.NORMAL,
        scheduledDate: new Date().toISOString().split('T')[0],
        requestNotes: '', fromScent: '', toScent: ''
      });
      setActiveTab('upcoming');
      alert('Visit scheduled successfully!');
    },
    onError: (error: any) => { alert(error.response?.data?.message || 'Failed to schedule visit'); }
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => rentalService.updateVisitStatus(id, data)
  });

  const deleteServiceVisitsMutation = useMutation({
    mutationFn: rentalService.deleteAllServiceVisits,
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['upcoming-visits'] });
      queryClient.invalidateQueries({ queryKey: ['visits-this-week-count'] });
      alert(`Successfully deleted ${response.count} SERVICE visits`);
    },
    onError: (error: any) => { alert(error.response?.data?.message || 'Failed to delete SERVICE visits'); }
  });

  const recordRefillMutation = useMutation({
    mutationFn: refillService.createRefillVisit,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['upcoming-visits'] });
      queryClient.invalidateQueries({ queryKey: ['visits-this-week-count'] });
      queryClient.invalidateQueries({ queryKey: ['refill-visits'] });
      setShowRefillFormModal(false);
      setRefillVisitData(null);
      alert('Refill visit recorded successfully!');
    },
    onError: (error: any) => { alert(error.response?.data?.message || 'Failed to record refill visit'); }
  });

  const contracts = contractsData?.data || [];
  const upcomingVisits = upcomingVisitsData || [];

  useEffect(() => {
    if (currentScentData?.data?.currentScent && visitForm.visitType === VisitType.SCENT_CHANGE) {
      setVisitForm(prev => ({ ...prev, fromScent: currentScentData.data.currentScent || '' }));
    }
  }, [currentScentData, visitForm.visitType, visitForm.contractId]);

  const rentedMachines = selectedContract?.machines?.filter(m => m.status === 'RENTED') || [];
  const selectedVisitRentedMachines = selectedVisitContract?.machines?.filter(m => m.status === 'RENTED') || [];

  const handleScheduleVisit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!visitForm.contractId || !visitForm.machineIds || visitForm.machineIds.length === 0 || !visitForm.scheduledDate) {
      alert('Please fill in all required fields');
      return;
    }
    const submitData: CreateVisitScheduleData = {
      contractId: visitForm.contractId,
      machineIds: visitForm.machineIds,
      visitType: visitForm.visitType!,
      priority: visitForm.priority!,
      scheduledDate: visitForm.scheduledDate,
      requestNotes: visitForm.requestNotes || undefined,
      fromScent: visitForm.fromScent || undefined,
      toScent: visitForm.toScent || undefined
    };
    scheduleVisitMutation.mutate(submitData);
  };

  const handleOpenStatusModal = (visit: any) => {
    setSelectedVisit(visit);
    setStatusForm({
      visitStatus: visit.visitStatus,
      issueResolved: visit.issueResolved || false,
      followupRequired: visit.followupRequired || false,
      completionNotes: visit.completionNotes || '',
      machineSelection: visit.machines?.map((m: any) => m.id) || [],
      machineIds: visit.machines?.map((m: any) => m.id) || []
    });
    setShowStatusModal(true);
  };

  const handleUpdateStatus = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedVisit) return;

    if (statusForm.visitStatus === VisitStatus.SCHEDULED && selectedVisit.visitStatus !== VisitStatus.SCHEDULED) {
      setShowStatusModal(false);
      setShowRescheduleModal(true);
      return;
    }

    if (statusForm.visitStatus === VisitStatus.NEEDS_FOLLOWUP) {
      const followupData = {
        contractId: selectedVisit.contract?.id || '',
        machineSelection: selectedVisit.machines?.map((m: any) => m.id) || [],
        machineIds: selectedVisit.machines?.map((m: any) => m.id) || [],
        visitType: selectedVisit.visitType,
        priority: VisitPriority.URGENT,
        scheduledDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        requestNotes: `Follow-up visit for ${selectedVisit.visitNumber}. Previous notes: ${statusForm.completionNotes || 'N/A'}`,
        fromScent: '',
        toScent: ''
      };
      setFollowupForm(followupData);
      updateStatusMutation.mutate(
        { id: selectedVisit.id, data: statusForm },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['upcoming-visits'] });
            queryClient.invalidateQueries({ queryKey: ['visits-this-week-count'] });
            setShowStatusModal(false);
            setShowFollowupModal(true);
          },
          onError: (error: any) => { alert(error.response?.data?.message || 'Failed to update visit status'); }
        }
      );
      return;
    }

    if (statusForm.visitStatus === VisitStatus.COMPLETED && selectedVisit.visitType === VisitType.REFILL) {
      updateStatusMutation.mutate(
        { id: selectedVisit.id, data: statusForm },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['upcoming-visits'] });
            queryClient.invalidateQueries({ queryKey: ['visits-this-week-count'] });
            setRefillVisitData({
              visitId: selectedVisit.id,
              contractId: selectedVisit.contract?.id,
              customerId: selectedVisit.customer?.id,
              customerName: selectedVisit.customer?.name,
              visitDate: selectedVisit.visitDate,
              machines: selectedVisit.machines || [],
              employeeId: selectedVisit.employee?.id
            });
            setShowStatusModal(false);
            setShowRefillFormModal(true);
            setStatusForm({ visitStatus: VisitStatus.IN_PROGRESS, issueResolved: false, followupRequired: false, completionNotes: '', machineSelection: [], machineIds: [] });
          },
          onError: (error: any) => { alert(error.response?.data?.message || 'Failed to update visit status'); }
        }
      );
      return;
    }

    updateStatusMutation.mutate(
      { id: selectedVisit.id, data: statusForm },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['upcoming-visits'] });
          queryClient.invalidateQueries({ queryKey: ['visits-this-week-count'] });
          setShowStatusModal(false);
          setSelectedVisit(null);
          setStatusForm({ visitStatus: VisitStatus.IN_PROGRESS, issueResolved: false, followupRequired: false, completionNotes: '', machineSelection: [], machineIds: [] });
          alert('Visit status updated successfully!');
        },
        onError: (error: any) => { alert(error.response?.data?.message || 'Failed to update visit status'); }
      }
    );
  };

  const handleReschedule = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedVisit) return;
    const updatedNotes = `${statusForm.completionNotes || ''}${statusForm.completionNotes ? '\n\n' : ''}Rescheduled from ${new Date(selectedVisit.visitDate).toLocaleDateString()} to ${new Date(rescheduleForm.newDate).toLocaleDateString()}. Reason: ${rescheduleForm.reason}`;
    updateStatusMutation.mutate(
      { id: selectedVisit.id, data: { visitStatus: VisitStatus.SCHEDULED, visitDate: rescheduleForm.newDate, completionNotes: updatedNotes, issueResolved: statusForm.issueResolved, followupRequired: statusForm.followupRequired } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['upcoming-visits'] });
          queryClient.invalidateQueries({ queryKey: ['visits-this-week-count'] });
          setShowRescheduleModal(false);
          setShowStatusModal(false);
          setSelectedVisit(null);
          setStatusForm({ visitStatus: VisitStatus.IN_PROGRESS, issueResolved: false, followupRequired: false, completionNotes: '', machineSelection: [], machineIds: [] });
          setRescheduleForm({ newDate: new Date().toISOString().split('T')[0], reason: '' });
          alert('Visit rescheduled successfully!');
        },
        onError: (error: any) => { alert(error.response?.data?.message || 'Failed to reschedule visit'); }
      }
    );
  };

  const handleFollowupSchedule = (e: React.FormEvent) => {
    e.preventDefault();
    if (!followupForm.contractId || !followupForm.machineIds || followupForm.machineIds.length === 0 || !followupForm.scheduledDate) {
      alert('Please fill in all required fields');
      return;
    }
    const submitData: CreateVisitScheduleData = {
      contractId: followupForm.contractId,
      machineIds: followupForm.machineIds,
      visitType: followupForm.visitType!,
      priority: followupForm.priority!,
      scheduledDate: followupForm.scheduledDate,
      requestNotes: followupForm.requestNotes || undefined,
      fromScent: followupForm.fromScent || undefined,
      toScent: followupForm.toScent || undefined
    };
    scheduleVisitMutation.mutate(submitData);
    setShowFollowupModal(false);
  };

  const getNextStatus = (currentStatus: VisitStatus): VisitStatus | null => {
    switch (currentStatus) {
      case VisitStatus.SCHEDULED:   return VisitStatus.IN_PROGRESS;
      case VisitStatus.IN_PROGRESS: return VisitStatus.COMPLETED;
      default: return null;
    }
  };

  const handleQuickStatusUpdate = (visit: any, newStatus: VisitStatus) => {
    updateStatusMutation.mutate(
      { id: visit.id, data: { visitStatus: newStatus } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['upcoming-visits'] });
          queryClient.invalidateQueries({ queryKey: ['visits-this-week-count'] });
          alert('Visit status updated successfully!');
        },
        onError: (error: any) => { alert(error.response?.data?.message || 'Failed to update visit status'); }
      }
    );
  };

  const handleMachineSelection = (machineId: string) => {
    const currentSelection = visitForm.machineSelection || [];
    const newSelection = currentSelection.includes(machineId)
      ? currentSelection.filter(id => id !== machineId)
      : [...currentSelection, machineId];
    setVisitForm(prev => ({ ...prev, machineSelection: newSelection, machineIds: newSelection }));
  };

  const handleStatusFormMachineSelection = (machineId: string) => {
    const currentSelection = statusForm.machineSelection || [];
    const newSelection = currentSelection.includes(machineId)
      ? currentSelection.filter(id => id !== machineId)
      : [...currentSelection, machineId];
    setStatusForm(prev => ({ ...prev, machineSelection: newSelection, machineIds: newSelection }));
  };

  const handleDeleteServiceVisits = () => {
    if (window.confirm('Are you sure you want to delete ALL SERVICE type visits? This action cannot be undone.')) {
      deleteServiceVisitsMutation.mutate();
    }
  };

  const handlePrintVisit = async (visit: any) => {
    try {
      const contractResponse = await rentalService.getRentalContract(visit.contract.id);
      const contractMachines = contractResponse.machines || [];
      const selectedMachineIds = visit.machines?.map((m: any) => m.id) || [];
      const otherMachines = contractMachines.filter((machine: any) => !selectedMachineIds.includes(machine.id));
      const printWindow = window.open('', '_blank');
      if (!printWindow) { alert('Please allow popups to print the visit document'); return; }
      const printContainer = printWindow.document.createElement('div');
      printWindow.document.body.appendChild(printContainer);
      const root = createRoot(printContainer);
      root.render(<VisitPrintDocument visit={visit} contractMachines={otherMachines} />);
      setTimeout(() => { printWindow.print(); }, 500);
    } catch (error) {
      console.error('Error printing visit:', error);
      alert('Failed to generate print document');
    }
  };

  const tabActive: React.CSSProperties = {
    background: 'linear-gradient(135deg, #d97706, #b45309)',
    color: '#1c2033',
    border: 'none',
    borderRadius: 4,
    padding: '8px 18px',
    fontFamily: "'Inter', sans-serif",
    fontSize: 10,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    cursor: 'pointer',
    fontWeight: 600,
  };

  const tabInactive: React.CSSProperties = {
    background: 'transparent',
    color: '#6b6a5c',
    border: '1px solid rgba(92,78,51,0.25)',
    borderRadius: 4,
    padding: '8px 18px',
    fontFamily: "'Inter', sans-serif",
    fontSize: 10,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    cursor: 'pointer',
  };

  if (!isOpen) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(7,7,15,0.85)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.25)', borderRadius: 8, width: '100%', maxWidth: 1100, maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

        {/* Header */}
        <div style={{ padding: '24px 32px', borderBottom: '1px solid rgba(92,78,51,0.15)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <h2 style={{ fontFamily: "'Inter', sans-serif", fontSize: 24, fontWeight: 600, color: '#1c2033', margin: 0 }}>
              Customer Visit Scheduling
            </h2>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#6b6a5c', cursor: 'pointer', padding: 4 }}>
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => setActiveTab('upcoming')} style={activeTab === 'upcoming' ? tabActive : tabInactive}>
              Upcoming Visits ({upcomingVisits.length})
            </button>
            <button onClick={() => setActiveTab('schedule')} style={activeTab === 'schedule' ? tabActive : tabInactive}>
              Schedule New Visit
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px 32px' }}>

          {/* ── Upcoming Visits Tab ────────────────────────────────────── */}
          {activeTab === 'upcoming' && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <h3 style={{ fontFamily: "'Inter', sans-serif", fontSize: 18, fontWeight: 600, color: '#1c2033', margin: 0 }}>
                  Upcoming Customer Visits
                </h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <button
                    onClick={handleDeleteServiceVisits}
                    disabled={deleteServiceVisitsMutation.isPending}
                    style={{ ...dangerBtn, opacity: deleteServiceVisitsMutation.isPending ? 0.6 : 1, cursor: deleteServiceVisitsMutation.isPending ? 'not-allowed' : 'pointer' }}
                    title="Delete all SERVICE type visits"
                  >
                    {deleteServiceVisitsMutation.isPending ? 'Deleting...' : 'Clear SERVICE Visits'}
                  </button>
                  <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#6b6a5c' }}>Days ahead:</span>
                  <select
                    value={daysAhead}
                    onChange={(e) => setDaysAhead(parseInt(e.target.value))}
                    style={{ ...inputStyle, width: 'auto' }}
                  >
                    <option value={3}>3 days</option>
                    <option value={7}>7 days</option>
                    <option value={14}>14 days</option>
                    <option value={30}>30 days</option>
                  </select>
                </div>
              </div>

              {upcomingLoading ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#6b6a5c', fontFamily: "'Inter', sans-serif", fontSize: 13 }}>
                  Loading upcoming visits...
                </div>
              ) : upcomingVisits.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#6b6a5c', fontFamily: "'Inter', sans-serif", fontSize: 13 }}>
                  No upcoming visits scheduled for the next {daysAhead} days
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={thStyle}>Visit #</th>
                        <th style={thStyle}>Date</th>
                        <th style={thStyle}>Customer</th>
                        <th style={thStyle}>Type</th>
                        <th style={thStyle}>Priority</th>
                        <th style={thStyle}>Status</th>
                        <th style={thStyle}>Machines</th>
                        <th style={thStyle}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {upcomingVisits.map((visit: any) => (
                        <tr
                          key={visit.id}
                          style={{ background: visit.priority === VisitPriority.URGENT ? 'rgba(192,80,77,0.04)' : 'transparent' }}
                          onMouseEnter={e => (e.currentTarget.style.background = visit.priority === VisitPriority.URGENT ? 'rgba(192,80,77,0.08)' : 'rgba(241,245,249,1)')}
                          onMouseLeave={e => (e.currentTarget.style.background = visit.priority === VisitPriority.URGENT ? 'rgba(192,80,77,0.04)' : 'transparent')}
                        >
                          <td style={{ ...tdStyle, color: '#d97706', fontWeight: 500 }}>{visit.visitNumber}</td>
                          <td style={tdStyle}>{new Date(visit.visitDate).toLocaleDateString()}</td>
                          <td style={tdStyle}>
                            <div style={{ color: '#1c2033' }}>{visit.customer?.name}</div>
                            {visit.customer?.phone && (
                              <div style={{ fontSize: 11, color: '#6b6a5c', marginTop: 2 }}>{visit.customer.phone}</div>
                            )}
                          </td>
                          <td style={tdStyle}>
                            <span style={getVisitTypeStyle(visit.visitType)}>{visit.visitType.replace('_', ' ')}</span>
                          </td>
                          <td style={tdStyle}>
                            <span style={{
                              padding: '3px 10px',
                              borderRadius: 12,
                              fontFamily: "'Inter', sans-serif",
                              fontSize: 9,
                              letterSpacing: '0.1em',
                              textTransform: 'uppercase',
                              display: 'inline-block',
                              background: visit.priority === VisitPriority.URGENT ? 'rgba(192,80,77,0.12)' : 'rgba(92,78,51,0.15)',
                              color: visit.priority === VisitPriority.URGENT ? '#c0504d' : '#8a8778',
                              border: visit.priority === VisitPriority.URGENT ? '1px solid rgba(192,80,77,0.3)' : '1px solid rgba(92,78,51,0.25)',
                            }}>
                              {visit.priority}
                            </span>
                          </td>
                          <td style={tdStyle}>
                            <span style={getStatusStyle(visit.visitStatus)}>{visit.visitStatus.replace('_', ' ')}</span>
                          </td>
                          <td style={tdStyle}>{visit.machines?.length || 0} machine(s)</td>
                          <td style={tdStyle}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              {getNextStatus(visit.visitStatus) && (
                                <button
                                  onClick={() => handleQuickStatusUpdate(visit, getNextStatus(visit.visitStatus)!)}
                                  disabled={updateStatusMutation.isPending}
                                  style={{ ...smallPrimaryBtn, opacity: updateStatusMutation.isPending ? 0.6 : 1, cursor: updateStatusMutation.isPending ? 'not-allowed' : 'pointer' }}
                                  title={`Mark as ${getNextStatus(visit.visitStatus)?.replace('_', ' ')}`}
                                >
                                  {visit.visitStatus === VisitStatus.SCHEDULED && 'Start'}
                                  {visit.visitStatus === VisitStatus.IN_PROGRESS && 'Complete'}
                                </button>
                              )}
                              <button
                                onClick={() => handleOpenStatusModal(visit)}
                                style={smallGhostBtn}
                                title="Update details"
                              >
                                Details
                              </button>
                              <button
                                onClick={() => handlePrintVisit(visit)}
                                style={smallGreenBtn}
                                title="Print visit work order"
                              >
                                Print
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ── Schedule New Visit Tab ─────────────────────────────────── */}
          {activeTab === 'schedule' && (
            <div>
              <h3 style={{ fontFamily: "'Inter', sans-serif", fontSize: 18, fontWeight: 600, color: '#1c2033', margin: '0 0 24px' }}>
                Schedule New Customer Visit
              </h3>
              <form onSubmit={handleScheduleVisit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                  {/* Contract */}
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={labelStyle}>Rental Contract <span style={{ color: '#c0504d' }}>*</span></label>
                    <select
                      value={visitForm.contractId || ''}
                      onChange={(e) => setVisitForm(prev => ({ ...prev, contractId: e.target.value, machineSelection: [], machineIds: [] }))}
                      style={inputStyle}
                      required
                    >
                      <option value="">Select Contract</option>
                      {contracts.map((contract: any) => (
                        <option key={contract.id} value={contract.id}>
                          {contract.contractNumber} - {contract.customer?.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Machine Multi-select */}
                  {visitForm.contractId && (
                    <div style={{ gridColumn: '1 / -1' }}>
                      <label style={labelStyle}>Machines <span style={{ color: '#c0504d' }}>*</span></label>
                      <div style={{ background: '#f4efe2', border: '1px solid rgba(92,78,51,0.25)', borderRadius: 4, padding: 12, maxHeight: 180, overflowY: 'auto' }}>
                        {rentedMachines.length === 0 ? (
                          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6b6a5c', margin: 0 }}>
                            No rented machines available for this contract
                          </p>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {rentedMachines.map((machine: any) => (
                              <label
                                key={machine.id}
                                style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '6px 8px', borderRadius: 4, background: visitForm.machineSelection?.includes(machine.id) ? 'rgba(239,232,213,0.6)' : 'transparent' }}
                                onMouseEnter={e => (e.currentTarget.style.background = 'rgba(239,232,213,0.6)')}
                                onMouseLeave={e => (e.currentTarget.style.background = visitForm.machineSelection?.includes(machine.id) ? 'rgba(239,232,213,0.6)' : 'transparent')}
                              >
                                <input
                                  type="checkbox"
                                  checked={visitForm.machineSelection?.includes(machine.id)}
                                  onChange={() => handleMachineSelection(machine.id)}
                                  style={{ accentColor: '#d97706', width: 14, height: 14 }}
                                />
                                <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#5b5a52' }}>
                                  {machine.serialNumber?.serialNumber} - {machine.serialNumber?.product?.name}
                                </span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                      <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c', marginTop: 4 }}>
                        Selected: {visitForm.machineSelection?.length || 0} machine(s)
                      </p>
                    </div>
                  )}

                  {/* Visit Type */}
                  <div>
                    <label style={labelStyle}>Visit Type <span style={{ color: '#c0504d' }}>*</span></label>
                    <select
                      value={visitForm.visitType || VisitType.REFILL}
                      onChange={(e) => setVisitForm(prev => ({ ...prev, visitType: e.target.value as VisitType }))}
                      style={inputStyle}
                      required
                    >
                      <option value={VisitType.REFILL}>Refill</option>
                      <option value={VisitType.SERVICE}>Service</option>
                      <option value={VisitType.SCENT_CHANGE}>Scent Change</option>
                      <option value={VisitType.SAMPLE_DELIVERY}>Sample Delivery</option>
                    </select>
                  </div>

                  {/* Priority */}
                  <div>
                    <label style={labelStyle}>Priority <span style={{ color: '#c0504d' }}>*</span></label>
                    <select
                      value={visitForm.priority || VisitPriority.NORMAL}
                      onChange={(e) => setVisitForm(prev => ({ ...prev, priority: e.target.value as VisitPriority }))}
                      style={inputStyle}
                      required
                    >
                      <option value={VisitPriority.NORMAL}>Normal</option>
                      <option value={VisitPriority.URGENT}>Urgent</option>
                    </select>
                  </div>

                  {/* Scheduled Date */}
                  <div>
                    <label style={labelStyle}>Visit Date <span style={{ color: '#c0504d' }}>*</span></label>
                    <input
                      type="date"
                      value={(visitForm.scheduledDate as string) || ''}
                      onChange={(e) => setVisitForm(prev => ({ ...prev, scheduledDate: e.target.value }))}
                      style={inputStyle}
                      required
                    />
                  </div>

                  {/* From Scent */}
                  {visitForm.visitType === VisitType.SCENT_CHANGE && (
                    <div>
                      <label style={labelStyle}>Current Scent</label>
                      <input
                        type="text"
                        value={visitForm.fromScent || ''}
                        onChange={(e) => setVisitForm(prev => ({ ...prev, fromScent: e.target.value }))}
                        placeholder="e.g., Lavender (if known)"
                        style={inputStyle}
                      />
                    </div>
                  )}

                  {/* To Scent */}
                  {visitForm.visitType === VisitType.SCENT_CHANGE && (
                    <div>
                      <label style={labelStyle}>New Scent</label>
                      <input
                        type="text"
                        value={visitForm.toScent || ''}
                        onChange={(e) => setVisitForm(prev => ({ ...prev, toScent: e.target.value }))}
                        placeholder="e.g., Rose (if known)"
                        style={inputStyle}
                      />
                    </div>
                  )}

                  {/* Notes */}
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={labelStyle}>Visit Notes</label>
                    <textarea
                      value={visitForm.requestNotes || ''}
                      onChange={(e) => setVisitForm(prev => ({ ...prev, requestNotes: e.target.value }))}
                      placeholder="Any special instructions or customer requests..."
                      rows={3}
                      style={{ ...inputStyle, resize: 'vertical' }}
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, paddingTop: 8, borderTop: '1px solid rgba(92,78,51,0.15)' }}>
                  <button
                    type="button"
                    onClick={() => setVisitForm({ contractId: '', machineSelection: [], machineIds: [], visitType: VisitType.REFILL, priority: VisitPriority.NORMAL, scheduledDate: new Date().toISOString().split('T')[0], requestNotes: '', fromScent: '', toScent: '' })}
                    style={ghostBtn}
                  >
                    Reset
                  </button>
                  <button
                    type="submit"
                    disabled={scheduleVisitMutation.isPending}
                    style={{ ...primaryBtn, opacity: scheduleVisitMutation.isPending ? 0.6 : 1, cursor: scheduleVisitMutation.isPending ? 'not-allowed' : 'pointer' }}
                  >
                    {scheduleVisitMutation.isPending ? 'Scheduling...' : 'Schedule Visit'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 32px', borderTop: '1px solid rgba(92,78,51,0.15)', display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={ghostBtn}>Close</button>
        </div>
      </div>

      {/* ── Status Update Sub-modal ─────────────────────────────────────── */}
      {showStatusModal && selectedVisit && (
        <SubModal zIndex={1060}>
          <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.25)', borderRadius: 8, width: '100%', maxWidth: 600, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ padding: '24px 28px', borderBottom: '1px solid rgba(92,78,51,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ fontFamily: "'Inter', sans-serif", fontSize: 20, fontWeight: 600, color: '#1c2033', margin: 0 }}>
                Update Visit Status — {selectedVisit.visitNumber}
              </h3>
              <button onClick={() => { setShowStatusModal(false); setSelectedVisit(null); }} style={{ background: 'none', border: 'none', color: '#6b6a5c', cursor: 'pointer', padding: 4 }}>
                <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleUpdateStatus} style={{ padding: 28 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {/* Visit Info */}
                <div style={{ background: '#faf7ee', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6, padding: 16 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12 }}>
                      <span style={{ color: '#6b6a5c' }}>Customer: </span>
                      <span style={{ color: '#1c2033', fontWeight: 500 }}>{selectedVisit.customer?.name}</span>
                    </div>
                    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12 }}>
                      <span style={{ color: '#6b6a5c' }}>Visit Date: </span>
                      <span style={{ color: '#1c2033', fontWeight: 500 }}>{new Date(selectedVisit.visitDate).toLocaleDateString()}</span>
                    </div>
                    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12 }}>
                      <span style={{ color: '#6b6a5c' }}>Type: </span>
                      <span style={getVisitTypeStyle(selectedVisit.visitType)}>{selectedVisit.visitType.replace('_', ' ')}</span>
                    </div>
                    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12 }}>
                      <span style={{ color: '#6b6a5c' }}>Status: </span>
                      <span style={getStatusStyle(selectedVisit.visitStatus)}>{selectedVisit.visitStatus.replace('_', ' ')}</span>
                    </div>
                  </div>
                </div>

                {/* Machine Selection */}
                {selectedVisitRentedMachines.length > 0 && (
                  <div>
                    <label style={labelStyle}>Machines for this Visit</label>
                    <div style={{ background: '#f4efe2', border: '1px solid rgba(92,78,51,0.25)', borderRadius: 4, padding: 10, maxHeight: 160, overflowY: 'auto' }}>
                      {selectedVisitRentedMachines.map((machine: any) => (
                        <label key={machine.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 6px', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={statusForm.machineSelection?.includes(machine.id)}
                            onChange={() => handleStatusFormMachineSelection(machine.id)}
                            style={{ accentColor: '#d97706', width: 14, height: 14 }}
                          />
                          <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#5b5a52' }}>
                            {machine.serialNumber?.serialNumber} - {machine.serialNumber?.product?.name}
                          </span>
                        </label>
                      ))}
                    </div>
                    <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c', marginTop: 4 }}>
                      Selected: {statusForm.machineSelection?.length || 0} machine(s)
                    </p>
                  </div>
                )}

                {/* New Status */}
                <div>
                  <label style={labelStyle}>New Status <span style={{ color: '#c0504d' }}>*</span></label>
                  <select
                    value={statusForm.visitStatus}
                    onChange={(e) => setStatusForm(prev => ({ ...prev, visitStatus: e.target.value as VisitStatus }))}
                    style={inputStyle}
                    required
                  >
                    <option value={VisitStatus.SCHEDULED}>Scheduled</option>
                    <option value={VisitStatus.IN_PROGRESS}>In Progress</option>
                    <option value={VisitStatus.COMPLETED}>Completed</option>
                    <option value={VisitStatus.NEEDS_FOLLOWUP}>Needs Followup</option>
                  </select>
                </div>

                {/* Issue Resolved */}
                {selectedVisit.visitType === VisitType.SERVICE && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={statusForm.issueResolved}
                      onChange={(e) => setStatusForm(prev => ({ ...prev, issueResolved: e.target.checked }))}
                      style={{ accentColor: '#d97706', width: 14, height: 14 }}
                    />
                    <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#6b6a5c' }}>Issue Resolved</span>
                  </label>
                )}

                {/* Followup Required */}
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={statusForm.followupRequired}
                    onChange={(e) => setStatusForm(prev => ({ ...prev, followupRequired: e.target.checked }))}
                    style={{ accentColor: '#d97706', width: 14, height: 14 }}
                  />
                  <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#6b6a5c' }}>Followup Required</span>
                </label>

                {/* Completion Notes */}
                <div>
                  <label style={labelStyle}>Completion Notes</label>
                  <textarea
                    value={statusForm.completionNotes}
                    onChange={(e) => setStatusForm(prev => ({ ...prev, completionNotes: e.target.value }))}
                    placeholder="Any notes about the visit (work performed, issues found, etc.)..."
                    rows={4}
                    style={{ ...inputStyle, resize: 'vertical' }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 20, paddingTop: 16, borderTop: '1px solid rgba(92,78,51,0.15)' }}>
                <button type="button" onClick={() => { setShowStatusModal(false); setSelectedVisit(null); }} style={ghostBtn}>Cancel</button>
                <button
                  type="submit"
                  disabled={updateStatusMutation.isPending}
                  style={{ ...primaryBtn, opacity: updateStatusMutation.isPending ? 0.6 : 1, cursor: updateStatusMutation.isPending ? 'not-allowed' : 'pointer' }}
                >
                  {updateStatusMutation.isPending ? 'Updating...' : 'Update Status'}
                </button>
              </div>
            </form>
          </div>
        </SubModal>
      )}

      {/* ── Reschedule Sub-modal ────────────────────────────────────────── */}
      {showRescheduleModal && selectedVisit && (
        <SubModal zIndex={1070}>
          <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.25)', borderRadius: 8, width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ padding: '24px 28px', borderBottom: '1px solid rgba(92,78,51,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ fontFamily: "'Inter', sans-serif", fontSize: 20, fontWeight: 600, color: '#1c2033', margin: 0 }}>
                Reschedule Visit — {selectedVisit.visitNumber}
              </h3>
              <button onClick={() => { setShowRescheduleModal(false); setShowStatusModal(true); }} style={{ background: 'none', border: 'none', color: '#6b6a5c', cursor: 'pointer', padding: 4 }}>
                <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleReschedule} style={{ padding: 28 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {/* Info Card */}
                <div style={{ background: '#faf7ee', border: '1px solid rgba(92,78,51,0.22)', borderRadius: 6, padding: 16 }}>
                  <h4 style={{ fontFamily: "'Inter', sans-serif", fontSize: 15, fontWeight: 600, color: '#d97706', margin: '0 0 12px' }}>Current Visit Details</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12 }}>
                      <span style={{ color: '#6b6a5c' }}>Customer: </span>
                      <span style={{ color: '#1c2033' }}>{selectedVisit.customer?.name}</span>
                    </div>
                    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12 }}>
                      <span style={{ color: '#6b6a5c' }}>Current Date: </span>
                      <span style={{ color: '#1c2033' }}>{new Date(selectedVisit.visitDate).toLocaleDateString()}</span>
                    </div>
                    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12 }}>
                      <span style={{ color: '#6b6a5c' }}>Type: </span>
                      <span style={{ color: '#5b5a52' }}>{selectedVisit.visitType.replace('_', ' ')}</span>
                    </div>
                    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12 }}>
                      <span style={{ color: '#6b6a5c' }}>Priority: </span>
                      <span style={{ color: '#5b5a52' }}>{selectedVisit.priority}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <label style={labelStyle}>New Visit Date <span style={{ color: '#c0504d' }}>*</span></label>
                  <input
                    type="date"
                    value={rescheduleForm.newDate}
                    onChange={(e) => setRescheduleForm(prev => ({ ...prev, newDate: e.target.value }))}
                    style={inputStyle}
                    required
                  />
                </div>

                <div>
                  <label style={labelStyle}>Reason for Rescheduling <span style={{ color: '#c0504d' }}>*</span></label>
                  <textarea
                    value={rescheduleForm.reason}
                    onChange={(e) => setRescheduleForm(prev => ({ ...prev, reason: e.target.value }))}
                    placeholder="e.g., Customer requested postponement, technician unavailable, etc."
                    rows={3}
                    style={{ ...inputStyle, resize: 'vertical' }}
                    required
                  />
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 20, paddingTop: 16, borderTop: '1px solid rgba(92,78,51,0.15)' }}>
                <button type="button" onClick={() => { setShowRescheduleModal(false); setShowStatusModal(true); }} style={ghostBtn}>Cancel</button>
                <button
                  type="submit"
                  disabled={updateStatusMutation.isPending}
                  style={{ ...primaryBtn, opacity: updateStatusMutation.isPending ? 0.6 : 1, cursor: updateStatusMutation.isPending ? 'not-allowed' : 'pointer' }}
                >
                  {updateStatusMutation.isPending ? 'Rescheduling...' : 'Confirm Reschedule'}
                </button>
              </div>
            </form>
          </div>
        </SubModal>
      )}

      {/* ── Follow-up Sub-modal ─────────────────────────────────────────── */}
      {showFollowupModal && selectedVisit && (
        <SubModal zIndex={1070}>
          <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.25)', borderRadius: 8, width: '100%', maxWidth: 700, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ padding: '24px 28px', borderBottom: '1px solid rgba(92,78,51,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, background: '#ffffff', zIndex: 1 }}>
              <h3 style={{ fontFamily: "'Inter', sans-serif", fontSize: 20, fontWeight: 600, color: '#1c2033', margin: 0 }}>
                Schedule Follow-up Visit for {selectedVisit.visitNumber}
              </h3>
              <button onClick={() => setShowFollowupModal(false)} style={{ background: 'none', border: 'none', color: '#6b6a5c', cursor: 'pointer', padding: 4 }}>
                <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div style={{ padding: 28 }}>
              {/* History Card */}
              <div style={{ background: '#faf7ee', border: '1px solid rgba(92,78,51,0.22)', borderRadius: 6, padding: 16, marginBottom: 24 }}>
                <h4 style={{ fontFamily: "'Inter', sans-serif", fontSize: 15, fontWeight: 600, color: '#d97706', margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Previous Visit Information
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12 }}>
                    <span style={{ color: '#6b6a5c' }}>Visit Number: </span>
                    <span style={{ color: '#1c2033' }}>{selectedVisit.visitNumber}</span>
                  </div>
                  <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12 }}>
                    <span style={{ color: '#6b6a5c' }}>Date: </span>
                    <span style={{ color: '#1c2033' }}>{new Date(selectedVisit.visitDate).toLocaleDateString()}</span>
                  </div>
                  <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12 }}>
                    <span style={{ color: '#6b6a5c' }}>Type: </span>
                    <span style={{ color: '#5b5a52' }}>{selectedVisit.visitType.replace('_', ' ')}</span>
                  </div>
                  <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12 }}>
                    <span style={{ color: '#6b6a5c' }}>Customer: </span>
                    <span style={{ color: '#1c2033' }}>{selectedVisit.customer?.name}</span>
                  </div>
                  {statusForm.completionNotes && (
                    <div style={{ gridColumn: '1 / -1', fontFamily: "'Inter', sans-serif", fontSize: 12 }}>
                      <div style={{ color: '#6b6a5c', marginBottom: 4 }}>Last Notes:</div>
                      <p style={{ background: '#f4efe2', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 4, padding: '8px 10px', margin: 0, color: '#5b5a52' }}>
                        {statusForm.completionNotes}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <form onSubmit={handleFollowupSchedule} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div>
                    <label style={labelStyle}>Follow-up Visit Type <span style={{ color: '#c0504d' }}>*</span></label>
                    <select
                      value={followupForm.visitType || VisitType.SERVICE}
                      onChange={(e) => setFollowupForm(prev => ({ ...prev, visitType: e.target.value as VisitType }))}
                      style={inputStyle}
                      required
                    >
                      <option value={VisitType.SERVICE}>Service</option>
                      <option value={VisitType.REFILL}>Refill</option>
                      <option value={VisitType.SCENT_CHANGE}>Scent Change</option>
                      <option value={VisitType.SAMPLE_DELIVERY}>Sample Delivery</option>
                    </select>
                  </div>

                  <div>
                    <label style={labelStyle}>Priority <span style={{ color: '#c0504d' }}>*</span></label>
                    <select
                      value={followupForm.priority || VisitPriority.URGENT}
                      onChange={(e) => setFollowupForm(prev => ({ ...prev, priority: e.target.value as VisitPriority }))}
                      style={inputStyle}
                      required
                    >
                      <option value={VisitPriority.NORMAL}>Normal</option>
                      <option value={VisitPriority.URGENT}>Urgent</option>
                    </select>
                  </div>

                  <div>
                    <label style={labelStyle}>Follow-up Visit Date <span style={{ color: '#c0504d' }}>*</span></label>
                    <input
                      type="date"
                      value={(followupForm.scheduledDate as string) || ''}
                      onChange={(e) => setFollowupForm(prev => ({ ...prev, scheduledDate: e.target.value }))}
                      style={inputStyle}
                      required
                    />
                  </div>

                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={labelStyle}>Follow-up Notes</label>
                    <textarea
                      value={followupForm.requestNotes || ''}
                      onChange={(e) => setFollowupForm(prev => ({ ...prev, requestNotes: e.target.value }))}
                      placeholder="Details about what needs to be done during follow-up..."
                      rows={4}
                      style={{ ...inputStyle, resize: 'vertical' }}
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, paddingTop: 16, borderTop: '1px solid rgba(92,78,51,0.15)' }}>
                  <button type="button" onClick={() => setShowFollowupModal(false)} style={ghostBtn}>Skip Follow-up</button>
                  <button
                    type="submit"
                    disabled={scheduleVisitMutation.isPending}
                    style={{ ...primaryBtn, opacity: scheduleVisitMutation.isPending ? 0.6 : 1, cursor: scheduleVisitMutation.isPending ? 'not-allowed' : 'pointer' }}
                  >
                    {scheduleVisitMutation.isPending ? 'Scheduling...' : 'Schedule Follow-up Visit'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </SubModal>
      )}

      {/* ── Refill Form Sub-modal ───────────────────────────────────────── */}
      {showRefillFormModal && refillVisitData && (
        <SubModal zIndex={1060}>
          <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.25)', borderRadius: 8, width: '100%', maxWidth: 900, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ padding: '20px 28px', borderBottom: '1px solid rgba(92,78,51,0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: '#ffffff', zIndex: 1 }}>
              <h2 style={{ fontFamily: "'Inter', sans-serif", fontSize: 20, fontWeight: 600, color: '#1c2033', margin: 0 }}>
                Record Refill Details
              </h2>
              <button
                onClick={() => {
                  if (window.confirm('Are you sure you want to skip recording refill details? The visit is already marked as complete.')) {
                    setShowRefillFormModal(false);
                    setRefillVisitData(null);
                    setSelectedVisit(null);
                  }
                }}
                style={{ background: 'none', border: 'none', color: '#6b6a5c', cursor: 'pointer', padding: 4 }}
              >
                <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div style={{ padding: 28 }}>
              <RefillForm
                visitData={refillVisitData}
                onSubmit={(data) => { recordRefillMutation.mutate(data); }}
                onCancel={() => {
                  if (window.confirm('Are you sure you want to cancel? The visit is already marked as complete.')) {
                    setShowRefillFormModal(false);
                    setRefillVisitData(null);
                    setSelectedVisit(null);
                  }
                }}
              />
            </div>
          </div>
        </SubModal>
      )}
    </div>
  );
};
